import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface AppDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
  height?: string;
  icon?: React.ReactNode;
}

/**
 * Reusable AppDialog component
 *
 * Single responsibility: Provide consistent dialog structure across the app
 *
 * Features:
 * - Fixed header with title and optional description
 * - Scrollable content area (children)
 * - Fixed footer for actions
 * - Configurable width and height
 * - Consistent padding and spacing
 *
 * @example
 * <AppDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Sign Lease Agreement"
 *   description="Please review and sign"
 *   maxWidth="4xl"
 *   footer={
 *     <DialogFooter>
 *       <Button onClick={handleSign}>Sign</Button>
 *     </DialogFooter>
 *   }
 * >
 *   <LeaseContent />
 * </AppDialog>
 */
export function AppDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  maxWidth = "2xl",
  height = "90vh",
  icon,
}: AppDialogProps) {
  const maxWidthClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`${maxWidthClasses[maxWidth]} flex flex-col p-0`}
        style={{ height }}
      >
        {/* Fixed Header */}
        <div className="px-6 pt-6 shrink-0">
          <DialogHeader>
            <DialogTitle className={icon ? "flex items-center gap-2" : ""}>
              {icon}
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription>{description}</DialogDescription>
            )}
          </DialogHeader>
        </div>

        {/* Scrollable Content */}
        <ScrollArea className="flex-1 px-6">
          <div className="space-y-6 pb-6">{children}</div>
        </ScrollArea>

        {/* Fixed Footer */}
        {footer && (
          <div className="px-6 pb-6 shrink-0 border-t pt-6">{footer}</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
